<template>
    <div>
        <h2>Jobs</h2>
        <b-grid
            caption="List of jobs"
            :tableOptions="auditJobStore.tableOptions"
            :filterableFields="auditJobStore.filterableFields"
            :filterOptions="auditJobStore.filterOptions"
            @optionChange="getData"
            :items="auditJobStore.items"
            :columns="columns"
            :loading="auditJobStore.loading"
            :show-filter="false"
            :show-paging="false">
            <template #beforeFilter>
                <button
                    type="button"
                    class="btn btn-primary"
                    v-if="canSelectIncidents"
                    @click.prevent="showCreateEditor()">
                    Select Incidents
                </button>
            </template>

            <template v-slot:cell.incidentNumber="{item, column}">
                <span>{{ item.incidentNumber }}</span>
            </template>

            <template v-slot:cell.dateRange="{item, column}">
                <span>{{ item.startMonth }} to {{ item.endMonth }}</span>
            </template>

            <template v-slot:cell.incidents-count="{item, column}">
                <span v-if="item.universeSize"
                    >{{ item.incidentsCount }} (of {{ item.universeSize }} found)
                    <span v-if="item.previouslySelectedCount > 0">
                        – {{ item.previouslySelectedCount }} previously selected</span
                    >
                </span>
            </template>

            <template v-slot:cell.status="{item, column}">
                <span>
                    {{ item.status }}
                </span>

                <i
                    class="bi bi-info-circle-fill text-info ms-2"
                    v-tooltip="item.comment"
                    v-if="item.comment"></i>
            </template>

            <template v-slot:cell.sampleSize="{item, column}">
                <span v-if="item.sampleSize">{{ item.sampleSize }}</span>
                <span v-else
                    >{{ item.percentUniverse }}%
                    <span v-if="item.percentUniverseMinCount && item.percentUniverseMaxCount">
                        ({{ item.percentUniverseMinCount }}-{{ item.percentUniverseMaxCount }} incidents)</span
                    >
                    <span
                        v-else
                        if="item.percentUniverseMaxCount">
                        ({{ item.percentUniverseMaxCount }} incidents max)</span
                    >
                </span>
            </template>

            <template #cell.actions="{item}">
                <button
                    @click="runJob(item.id)"
                    type="button"
                    v-if="canRunJob"
                    class="btn btn-sm me-2 btn-primary">
                    Run
                </button>

                <button
                    @click="deleteJob(item.id)"
                    type="button"
                    v-if="canDelete(item)"
                    class="btn btn-sm btn-danger">
                    Delete
                </button>
                <span v-else></span>
            </template>
        </b-grid>
    </div>
    <form-modal
        title="Select Incidents"
        v-model="isShowCreateEditor"
        @submit="createJob"
        :loading="loading">
        <fieldset class="border-bottom mb-3">
            <legend class="reset">Specify Incident Universe</legend>
            <div class="mb-3 row">
                <div class="col-form-label col-sm-3">Date Range:</div>
                <div class="col-sm-9">
                    <div class="row">
                        <div class="col">
                            <label
                                for="startMonth"
                                class="col-form-label"
                                >Start Date:</label
                            >
                            <b-date-picker
                                name="startMonth"
                                :month-picker="true"
                                :error="errors.startMonth"
                                v-model="startMonth"></b-date-picker>
                        </div>
                        <div class="col">
                            <label
                                for="endMonth"
                                class="col-form-label"
                                >End Date:</label
                            >

                            <b-date-picker
                                :month-picker="true"
                                name="endMonth"
                                :error="errors.endMonth"
                                v-model="endMonth"></b-date-picker>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <div class="row">
                    <div class="form-label col-sm-3">Incident Types:</div>
                    <div class="col-sm-9">
                        <div class="row">
                            <div class="col-3">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        value="A"
                                        v-model="auditJob.incidentTypes"
                                        id="groupA" />
                                    <label
                                        class="form-check-label"
                                        for="groupA">
                                        Group A
                                    </label>
                                </div>
                            </div>
                            <div class="col-3">
                                <div class="form-check">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        value="B"
                                        v-model="auditJob.incidentTypes"
                                        id="groupB" />
                                    <label
                                        class="form-check-label"
                                        for="groupB">
                                        Group B
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mb-3 mt-3 row">
                    <div class="col-sm-3">
                        <label
                            for="offenseTypes"
                            class="col-form-label d-block"
                            >Offense(s):</label
                        >
                        <small id="offenseTypesHelp">Select one or more offenses, or make no selection for all</small>
                    </div>
                    <div class="col-sm-9">
                        <select
                            id="offenseTypes"
                            v-model="auditJob.offenseTypes"
                            multiple
                            :disabled="auditJob.incidentTypes.length == 0"
                            class="form-select"
                            aria-describedby="offenseTypesHelp">
                            <option
                                v-for="item in offenseStore.items"
                                :key="item.OffenseCode"
                                :value="item.OffenseCode">
                                {{ item.OffenseLabel }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </fieldset>

        <fieldset>
            <legend class="reset">Random Selection from Universe</legend>
            <div class="container">
                <div class="row mb-3 align-items-center">
                    <div class="col-md-3 form-check">
                        <input
                            class="form-check-input"
                            type="radio"
                            name="measureType"
                            id="measureTypeNumber"
                            v-model="auditJob.measureType"
                            :value="0" />
                        <label
                            class="form-check-label"
                            for="measureTypeNumber">
                            Fixed count of incidents:
                        </label>
                    </div>
                    <div class="col-sm-4">
                        <text-input
                            v-model="auditJob.sampleSize"
                            aria-label="Sample Size"
                            name="sampleSize"
                            :min="0"
                            type="number"
                            :disabled="auditJob.measureType != 0"
                            id="sampleSize"
                            class="form-control"
                            :error="errors.sampleSize"></text-input>
                    </div>
                </div>
                <div class="row mb-3 align-items-center">
                    <div class="col-md-3 form-check">
                        <div class="">
                            <input
                                class="form-check-input"
                                type="radio"
                                name="measureType"
                                v-model="auditJob.measureType"
                                :value="1"
                                id="measureTypePercent" />
                            <label
                                class="form-check-label"
                                for="measureTypePercent">
                                Percent of universe:
                            </label>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="input-group">
                            <text-input
                                v-model="auditJob.percentUniverse"
                                aria-label="Percent of universe"
                                name="percentUniverse"
                                type="number"
                                :min="0"
                                id="percentUniverse"
                                class="form-control"
                                :disabled="auditJob.measureType != 1"
                                :error="errors.percentUniverse">
                                <template #append>
                                    <span class="input-group-text">%</span>
                                </template>
                            </text-input>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-3 text-end">
                        <label
                            for="percentUniverseMinCount"
                            class="col-form-label"
                            >Min Count:</label
                        >
                    </div>
                    <div class="col-sm-4">
                        <text-input
                            type="number"
                            :min="0"
                            v-model="auditJob.percentUniverseMinCount"
                            id="percentUniverseMinCount"
                            :disabled="auditJob.measureType != 1"
                            class="form-control"
                            :error="errors.percentUniverseMinCount"></text-input>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-3 text-end">
                        <label
                            for="percentUniverseMaxCount"
                            class="col-form-label"
                            >Max Count:</label
                        >
                    </div>
                    <div class="col-sm-4">
                        <text-input
                            type="number"
                            v-model="auditJob.percentUniverseMaxCount"
                            :disabled="auditJob.measureType != 1"
                            id="percentUniverseMaxCount"
                            class="form-control"
                            :error="errors.percentUniverseMaxCount"></text-input>
                    </div>
                </div>
            </div>
        </fieldset>
    </form-modal>
</template>
<script lang="ts" setup>
import {BTableColumn} from "../BTable/BTable.types";
import TextInput from "../Fields/TextInput.vue";
import {computed, onMounted, reactive, ref, watch} from "vue";
import {useOffenseStore} from "@/stores/offenseStore";
import {useAuditJobStore} from "@/stores/auditJobStore";
import {AuditJobCreate, Audit} from "@/audit.types";
import {debounce} from "vue-debounce";
import {number, object, date} from "yup";
import {useFormValidation} from "../../validation";
import BGrid from "../BGrid/BGrid.vue";
import {convertToUTC} from "@/utils";
import {BInput, BDatePicker} from "./../Fields";
var updateData = debounce(() => getData(), "15000ms");
import {FormModal} from "../Misc";

import {useConfirmDialog} from "@/composable/useConfirmDialog";
import {storeToRefs} from "pinia";
const {confirm} = useConfirmDialog();

const props = defineProps<{
    id: number;
    audit: Audit;
    runAudits: boolean;
    createAudits: boolean;
}>();

const auditJob = ref<AuditJobCreate>({
    auditId: null,
    startMonth: props.audit.startMonth,
    endMonth: props.audit.endMonth,
    incidentTypes: [],
    offenseTypes: [],
    sampleSize: null,
    measureType: 0,
    percentUniverse: null,
    percentUniverseMinCount: 0,
    percentUniverseMaxCount: 50,
} as AuditJobCreate);

const schema = computed(() =>
    object<AuditJobCreate>({
        startMonth: date().required().label("Start Month").min(props.audit.startMonth),
        endMonth: date()
            .required()
            .label("End Month")
            .test(
                "maxDate-validation",
                `Start Month must be no later than ${props.audit.endMonth}`,
                async (endMonth) => {
                    var e = convertToUTC(new Date(props.audit.endMonth));
                    e.setMonth(e.getMonth() + 1);
                    return e >= endMonth;
                }
            )

            .when("startMonth", (startMonth, schema) => {
                if (auditJob.value.startMonth) {
                    const dayAfter = new Date(auditJob.value.startMonth.getTime() + 86400000);
                    return schema.min(dayAfter, "End month has to be no earlier than start month");
                }
                return schema;
            }),
        sampleSize: number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .when("measureType", {
                is: 0,
                then: (schema) => {
                    return schema.required().min(1);
                },
            })
            .label("Sample Size"),
        percentUniverse: number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .when("measureType", {
                is: 1,
                then: (schema) => {
                    return schema.required().min(1);
                },
            })
            .label("Percent of Universe"),
        percentUniverseMinCount: number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .min(0)
            .label("Min Count"),
        percentUniverseMaxCount: number()
            .nullable()
            .transform((value) => (isNaN(value) ? undefined : value))
            .when("measureType", {
                is: 1,
                then: (schema) => {
                    return schema.required().min(1);
                },
            })
            .when("percentUniverseMinCount", ([percentUniverseMinCount], schema) =>
                percentUniverseMinCount > 0 ? schema.moreThan(auditJob.value.percentUniverseMinCount!) : schema
            )
            .label("Max Count"),
    })
);

watch(
    () => auditJob.value.incidentTypes,
    async () => {
        await offenseStore.list(auditJob.value.incidentTypes.join(","));
    }
);

const auditJobStore = useAuditJobStore();
const {loading} = storeToRefs(auditJobStore);
const offenseStore = useOffenseStore();

const canSelectIncidents = computed(() => props.runAudits && props.audit.status != "Closed");
const canDelete = function (item) {
    return item.status == "Queued";
};

const columns = reactive<BTableColumn[]>([
    {
        key: "job-id",
        text: "Job #",
        sortable: false,
        value: (item) => item["id"],
    },
    {
        key: "status",
        text: "Status",
        sortable: false,
        value: (item) => item["status"],
    },
    {
        key: "dateRange",
        text: "Date Range",
        sortable: false,
        value: (item) => item,
    },
    {
        key: "incidentTypes",
        text: "Incident Groups",
        sortable: false,
        value: (item) => item["incidentTypes"],
    },
    {
        key: "offenseTypes",
        text: "Offense",
        sortable: false,
        value: (item) => item["offenseTypes"],
    },
    {
        key: "sampleSize",
        text: "Sample Size",
        sortable: false,
        value: (item) => item["sampleSize"],
    },
    {
        key: "incidents-count",
        text: "Incidents Selected",
        sortable: true,
        value: (item) => item["incidentsCount"],
    },
    {
        key: "actions",
        text: "",
        sortable: false,
        value: (item) => 2,
    },
]);

const {handleSubmit, errors, resetValidation} = useFormValidation();

const createJob = async () => {
    if (await handleSubmit(schema.value, auditJob.value)) {
        auditJob.value.auditId = props.id;
        await auditJobStore.create(auditJob.value);
        hideCreateEditor();
        await getData();

        auditJob.value = {
            sampleSize: null,
            incidentTypes: [],
            offenseTypes: [],
            measureType: 0,
            percentUniverseMinCount: 0,
            percentUniverseMaxCount: 50,
        } as unknown as AuditJobCreate;
    }
};
const canRunJob = ref(false);
const isShowCreateEditor = ref(false);
const showCreateEditor = () => {
    resetValidation();
    isShowCreateEditor.value = true;
    initDates();
};
const hideCreateEditor = () => {
    isShowCreateEditor.value = false;
};

const runJob = async (id: number) => {
    await auditJobStore.run(id);
};

const deleteJob = async (id: number) => {
    const shouldProceed = await confirm("Delete Confirmation", "Are You Sure?");
    if (shouldProceed) {
        auditJobStore.deleteItem(id);
        await getData();
    }
};

const getData = async () => {
    await auditJobStore.list(props.audit.id);
    await updateData();
};
const startMonth = ref();
const endMonth = ref();

watch(startMonth, (newStartMonth) => {
    if (newStartMonth) {
        auditJob.value!.startMonth = convertToUTC(new Date(newStartMonth.year, newStartMonth.month, 1));
    }
});

watch(endMonth, (newEndMonth) => {
    if (newEndMonth) {
        auditJob.value!.endMonth = convertToUTC(new Date(newEndMonth.year, newEndMonth.month + 1, 0));
    }
});
onMounted(async () => {
    await getData();

    initDates();
});

function initDates() {
    let date = convertToUTC(new Date(props.audit.startMonth));

    startMonth.value = {
        month: date.getMonth(),
        year: date.getFullYear(),
    };

    date = convertToUTC(new Date(props.audit.endMonth));

    endMonth.value = {
        month: date.getMonth(),
        year: date.getFullYear(),
    };
}
</script>
<style scoped>
.reset {
    font-size: 1.1em;
    margin-bottom: 1.2em;
    font-weight: 600;
}

small {
    font-size: 0.775em;
}
</style>
